'use client';

import React from 'react';
import { cn } from '../lib/utils';
import {
  fieldControlClassName,
  fieldDensityClassName,
  type FieldControlSize,
  type FieldDensity,
} from './input';
import {
  formatPartialTimeInput,
  parseTimeInput,
  timeEntryValidationMessage,
} from './time-entry-helpers';

// Re-export pure helpers so existing `from '.../time-entry-input'` imports keep working.
export {
  HH_MM_RE,
  parseTimeInput,
  formatPartialTimeInput,
  timeEntryValidationMessage,
} from './time-entry-helpers';

export interface TimeEntryInputProps {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: React.AriaAttributes['aria-invalid'];
  density?: FieldDensity;
  size?: FieldControlSize;
  className?: string;
  'data-testid'?: string;
}

export function TimeEntryInput({
  value,
  onChange,
  disabled,
  required,
  id,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  density,
  size,
  className,
  'data-testid': dataTestId,
}: TimeEntryInputProps) {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [text, setText] = React.useState(value);
  const [touched, setTouched] = React.useState(false);
  const messageId = React.useId();
  const lastExternal = React.useRef(value);

  React.useEffect(() => {
    if (value === lastExternal.current) return;
    lastExternal.current = value;
    setText(value);
  }, [value]);

  const validationMessage = touched ? timeEntryValidationMessage(text, { required }) : null;
  const showInvalid = Boolean(validationMessage);

  React.useEffect(() => {
    inputRef.current?.setCustomValidity(validationMessage ?? '');
  }, [validationMessage]);

  function update(nextRaw: string) {
    const next = formatPartialTimeInput(nextRaw);
    setText(next);
    // Report upward as soon as the text is a complete, unambiguous time.
    //
    // The native input this replaced raised change on every edit, so a form
    // that read its value without waiting for blur still saw the latest time.
    // Committing only on blur reintroduced a gap: pressing Enter to submit
    // never blurs, so a freshly typed time would not have reached the parent
    // and the OLD value would be saved. On a field that determines pay that is
    // a wage error, not a UX wrinkle.
    //
    // Partial text is still withheld — `parseTimeInput` returns null for it —
    // so the parent never sees a half-typed value.
    const parsed = parseTimeInput(next);
    if (parsed !== null) {
      lastExternal.current = parsed;
      onChange(parsed);
      return;
    }

    // Unparseable text must also RETRACT any value already reported upward.
    //
    // Typing commits as soon as the text parses, so "7" reports 07:00. The very
    // next keystroke on the way to 7:30 makes the text "73", which does not
    // parse — and without this the parent kept holding 07:00 while the field
    // displayed "73". The form's hidden value and the visible field then
    // disagreed about a value that sets pay. Clearing keeps the displayed text
    // the single truth: if the box does not show a valid time, no time is
    // reported.
    if (lastExternal.current !== '') {
      lastExternal.current = '';
      onChange('');
    }
  }

  function normaliseOnBlur(raw: string) {
    const formatted = formatPartialTimeInput(raw);
    const parsed = parseTimeInput(formatted);
    if (parsed !== null) {
      lastExternal.current = parsed;
      setText(parsed);
      onChange(parsed);
      return;
    }
    setText(formatted);
    // Same retraction on blur, for text that never became parseable.
    if (lastExternal.current !== '') {
      lastExternal.current = '';
      onChange('');
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="HH:MM"
        value={text}
        maxLength={5}
        disabled={disabled}
        required={required}
        aria-label={ariaLabel}
        aria-invalid={showInvalid || ariaInvalid}
        aria-describedby={
          [ariaDescribedBy, showInvalid ? messageId : undefined].filter(Boolean).join(' ') ||
          undefined
        }
        data-testid={dataTestId}
        data-time-entry-input
        className={cn(
          fieldControlClassName,
          fieldDensityClassName(density, size),
          className,
          // AFTER `className` deliberately. tailwind-merge resolves conflicts by
          // last-wins, and call sites pass their own border utility (the
          // create-shift form passes `border-control-border`). With the invalid
          // classes first, the caller's border silently won and an invalid entry
          // rendered identically to a valid one — a field that had rejected the
          // typed text looked completely normal.
          showInvalid && 'border-status-danger-fg focus:border-status-danger-fg focus:ring-focus',
        )}
        title={validationMessage ?? undefined}
        onChange={(event) => update(event.target.value)}
        onBlur={() => {
          setTouched(true);
          normaliseOnBlur(text);
        }}
        onKeyDown={(event) => {
          // Enter commonly submits the surrounding form and does NOT blur, so
          // normalise here too rather than relying on focus ever leaving.
          if (event.key === 'Enter') {
            setTouched(true);
            normaliseOnBlur(text);
          }
        }}
      />
      {showInvalid && (
        <p id={messageId} className="mt-1 text-caption text-status-danger-fg">
          {validationMessage}
        </p>
      )}
    </>
  );
}
